import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, ImageIcon, Loader2, Settings, Save, Check, RefreshCcw, X, ExternalLink, ChevronLeft, ChevronRight, ShoppingCart, ZoomIn } from 'lucide-react';
import ImageModal from './components/ImageModal';
import { formatResizeDisplay, formatTokenCount, getItemExplanation, getFileExtension, getFileExtensionFromUrl, BOUNDING_BOX_COLORS } from './utils/formatters';
import { useRuntimeSettings } from './context/RuntimeSettingsContext';

const AnalysisPage = () => {
  const colors = BOUNDING_BOX_COLORS;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const {
    modelName,
    setModelName,
    resizeMode,
    setResizeMode,
    compressionMode,
    setCompressionMode,
    useEbayRag,
    setUseEbayRag,
    useNotesRag,
    setUseNotesRag,
  } = useRuntimeSettings();
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [isChangingEbayRag, setIsChangingEbayRag] = useState(false);
  const [isChangingNotesRag, setIsChangingNotesRag] = useState(false);
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalNames, setOriginalNames] = useState({});
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingNameId, setUpdatingNameId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [ebayResults, setEbayResults] = useState(null);
  const [ebayLoading, setEbayLoading] = useState(false);
  const [resultImageErrors, setResultImageErrors] = useState({});
  const fileInputRef = useRef(null);
  const previewUrlsRef = useRef([]);
  const heicConverterPromiseRef = useRef(null);
  const isHeicFile = (file) => {
    const extension = getFileExtension(file?.name || '');
    const type = String(file?.type || '').toLowerCase();
    return extension === 'heic' || extension === 'heif' || type.includes('heic') || type.includes('heif');
  };
  const ALLOWED_UPLOAD_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'heic']);
  const ALLOWED_UPLOAD_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/heic', 'image/heif']);
  const isAllowedUploadFile = (file) => {
    const extension = getFileExtension(file?.name || '');
    const mimeType = String(file?.type || '').toLowerCase();
    if (extension) {
      return ALLOWED_UPLOAD_EXTENSIONS.has(extension);
    }
    return ALLOWED_UPLOAD_MIME_TYPES.has(mimeType);
  };
  const splitUploadFilesByType = (files = []) => {
    const valid = [];
    const invalid = [];
    files.forEach((file) => {
      if (isAllowedUploadFile(file)) {
        valid.push(file);
      } else {
        invalid.push(file);
      }
    });
    return { valid, invalid };
  };
  const alertInvalidFiles = (invalidFiles = []) => {
    if (!invalidFiles.length) return;
    const invalidNames = invalidFiles
      .map((file) => file?.name || 'unknown file')
      .slice(0, 8)
      .join(', ');
    const suffix = invalidFiles.length > 8 ? ', ...' : '';
    alert(`Only JPG, PNG, and HEIC files are allowed.\nIgnored: ${invalidNames}${suffix}`);
  };
  const buildPreviewId = (file) => `${Date.now()}-${file?.name || 'image'}-${Math.random().toString(36).slice(2, 8)}`;
  const getHeicConverter = async () => {
    if (!heicConverterPromiseRef.current) {
      heicConverterPromiseRef.current = import('heic2any').then((heic2anyModule) => (
        heic2anyModule.default || heic2anyModule
      ));
    }
    return heicConverterPromiseRef.current;
  };
  const convertHeicToPreviewBlob = async (file) => {
    const heic2any = await getHeicConverter();
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    });
    if (Array.isArray(converted)) {
      return converted[0];
    }
    return converted;
  };
  const buildPreview = (file) => {
    const extension = getFileExtension(file?.name || '');
    const heic = isHeicFile(file);

    return {
      id: buildPreviewId(file),
      url: URL.createObjectURL(file),
      name: file?.name || 'image',
      extension,
      isHeic: heic,
      isConverting: heic,
      hasPreviewError: false,
    };
  };

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((preview) => {
        if (preview?.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, []);

  const convertHeicPreview = async (previewId, file) => {
    try {
      const convertedBlob = await convertHeicToPreviewBlob(file);
      if (!(convertedBlob instanceof Blob)) {
        throw new Error('HEIC conversion returned no blob');
      }
      const convertedUrl = URL.createObjectURL(convertedBlob);
      setPreviewUrls((prev) => {
        let matched = false;
        const next = prev.map((preview) => {
          if (preview.id !== previewId) return preview;
          matched = true;
          if (preview.url && preview.url !== convertedUrl) {
            URL.revokeObjectURL(preview.url);
          }
          return {
            ...preview,
            url: convertedUrl,
            isConverting: false,
            hasPreviewError: false,
          };
        });
        if (!matched) {
          URL.revokeObjectURL(convertedUrl);
        }
        return next;
      });
    } catch (error) {
      console.warn(`Failed to convert ${file?.name || 'HEIC file'} for preview:`, error);
      setPreviewUrls((prev) => prev.map((preview) => (
        preview.id === previewId
          ? { ...preview, isConverting: false }
          : preview
      )));
    }
  };

  const appendFiles = (files) => {
    setSelectedFiles(prev => [...prev, ...files]);
    const newPreviews = files.map((file) => buildPreview(file));
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    setAnalysisResult(null);
    newPreviews.forEach((preview, index) => {
      if (preview.isHeic) {
        convertHeicPreview(preview.id, files[index]);
      }
    });
  };

  const handleModelChange = (newModel) => {
    setIsChangingModel(true);
    setModelName(newModel);
    setIsChangingModel(false);
  };

  const handleEbayRagChange = (nextValue) => {
    setIsChangingEbayRag(true);
    setUseEbayRag(Boolean(nextValue));
    setIsChangingEbayRag(false);
  };

  const handleNotesRagChange = (nextValue) => {
    setIsChangingNotesRag(true);
    setUseNotesRag(Boolean(nextValue));
    setIsChangingNotesRag(false);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { valid, invalid } = splitUploadFilesByType(files);
    alertInvalidFiles(invalid);
    if (valid.length > 0) {
      appendFiles(valid);
    }
    e.target.value = '';
  };

  const markPreviewError = (index) => {
    setPreviewUrls(prev =>
      prev.map((preview, i) => (
        i === index ? { ...preview, hasPreviewError: true } : preview
      ))
    );
  };

  const removeFile = (index) => {
    const previewToRemove = previewUrls[index];
    if (previewToRemove?.url) {
      URL.revokeObjectURL(previewToRemove.url);
    }
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    setResultImageErrors((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const errorIndex = Number(key);
        if (!Number.isNaN(errorIndex)) {
          if (errorIndex < index) next[errorIndex] = value;
          if (errorIndex > index) next[errorIndex - 1] = value;
        }
      });
      return next;
    });
    if (currentImageIndex >= previewUrls.length - 1 && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    const { valid, invalid } = splitUploadFilesByType(files);
    alertInvalidFiles(invalid);
    if (valid.length > 0) {
      appendFiles(valid);
    }
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    setAnalysisResult(null);
    setCurrentImageIndex(0);
    setResultImageErrors({});

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('file', file);
      });
      formData.append('model', modelName);
      formData.append('resize_mode', resizeMode);
      formData.append('compression_mode', compressionMode);
      formData.append('use_ebay_rag', String(useEbayRag));
      formData.append('use_notes_rag', String(useNotesRag));

      const response = await axios.post('/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Extract the parsed data if it's coming as a string from AI
      let resultData = response.data;
      if (!resultData.items && resultData.data && typeof resultData.data === 'string') {
        try {
          const cleanedJson = resultData.data.replace(/```json|```/g, '').trim();
          resultData.items = JSON.parse(cleanedJson);
        } catch (e) {
          console.error("Failed to parse data string", e);
          resultData.items = [];
        }
      } else if (!resultData.items) {
        resultData.items = resultData.data || [];
      }
      resultData.items = (Array.isArray(resultData.items) ? resultData.items : []).map((item) => {
        if (!item || typeof item !== 'object') return item;
        const explanation = getItemExplanation(item);
        return {
          ...item,
          explanation,
          margin_analysis: explanation,
        };
      });
      setAnalysisResult(resultData);
      
      // Initialize original prices and names for dirty state tracking
      const prices = {};
      const names = {};
      resultData.items.forEach(item => {
        if (item.id) {
          prices[item.id] = item.actual_price;
          names[item.id] = item.manual_name || '';
        }
      });
      setOriginalPrices(prices);
      setOriginalNames(names);

      // Auto-trigger eBay image search
      if (resultData.blob_urls && resultData.blob_urls.length > 0) {
        const itemIds = (resultData.items || []).filter(i => i.id).map(i => i.id);
        searchEbay(resultData.blob_urls, itemIds);
      }
    } catch (error) {
      console.error('Error analyzing image:', error);

      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.message;
      const serverDetail = error?.response?.data?.detail;
      const fallbackMessage = error?.message || 'Unknown error';

      let explicitError = serverDetail || serverMessage || fallbackMessage;
      if (status) {
        explicitError = `HTTP ${status}: ${explicitError}`;
      }
      if (!error?.response && error?.request) {
        explicitError = 'No response from server. Check backend availability and network connectivity.';
      }

      alert(`Error analyzing image: ${explicitError}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setAnalysisResult({
      ...analysisResult,
      items: analysisResult.items.map(item => 
        item.id === id ? { ...item, actual_price: value } : item
      )
    });
  };

  const handleManualNameChange = (id, value) => {
    setAnalysisResult({
      ...analysisResult,
      items: analysisResult.items.map(item => 
        item.id === id ? { ...item, manual_name: value } : item
      )
    });
  };

  const updatePrice = async (id, price) => {
    setUpdatingId(id);
    try {
      await axios.post('/update_price', {
        id: id,
        actual_price: parseFloat(price)
      });
      setOriginalPrices({
        ...originalPrices,
        [id]: parseFloat(price)
      });
    } catch (error) {
      console.error("Error saving price:", error);
      alert("Failed to save price.");
    } finally {
      setUpdatingId(null);
    }
  };

  const searchEbay = async (blobUrls, itemIds) => {
    setEbayLoading(true);
    setEbayResults(null);
    try {
      const response = await axios.post('/ebay_search', {
        blob_urls: blobUrls,
        item_ids: itemIds
      });
      if (response.data.status === 'success') {
        setEbayResults(response.data.ebay_results);
      }
    } catch (error) {
      console.error('Error searching eBay:', error);
    } finally {
      setEbayLoading(false);
    }
  };

  const updateItemName = async (id, name) => {
    setUpdatingNameId(id);
    try {
      await axios.post('/update_item_name', {
        id: id,
        item_name: name
      });
      setOriginalNames({
        ...originalNames,
        [id]: name
      });
    } catch (error) {
      console.error("Error saving item name:", error);
      alert("Failed to save item name.");
    } finally {
      setUpdatingNameId(null);
    }
  };

  const markResultImageError = (index) => {
    setResultImageErrors((prev) => ({ ...prev, [index]: true }));
  };
  const analysisServerImageUrls = analysisResult
    ? (
      Array.isArray(analysisResult.blob_urls) && analysisResult.blob_urls.length > 0
        ? analysisResult.blob_urls
        : (analysisResult.blob_url ? [analysisResult.blob_url] : [])
    )
    : [];
  const analysisImageCount = Math.max(analysisServerImageUrls.length, previewUrls.length);
  const usePreviewFirst = (
    previewUrls.length > 0 &&
    analysisServerImageUrls.length > 0 &&
    previewUrls.length === analysisServerImageUrls.length
  );
  const analysisDisplayImageUrls = Array.from({ length: analysisImageCount }, (_, index) => {
    const serverUrl = analysisServerImageUrls[index] || '';
    const previewEntry = previewUrls[index] || {};
    const previewUrl = previewEntry?.url || '';
    const serverExtension = getFileExtensionFromUrl(serverUrl);
    if (
      usePreviewFirst &&
      previewUrl &&
      !previewEntry?.isConverting &&
      !previewEntry?.hasPreviewError
    ) {
      return previewUrl;
    }
    const shouldUsePreview = Boolean(resultImageErrors[index]) || serverExtension === 'heic' || serverExtension === 'heif';
    if (shouldUsePreview && previewUrl) {
      return previewUrl;
    }
    return serverUrl || previewUrl || '';
  });
  const analysisUsage = (analysisResult && typeof analysisResult.usage === 'object') ? analysisResult.usage : {};
  const inputTokens = analysisUsage.prompt_tokens ?? analysisUsage.input_tokens ?? null;
  const outputTokens = analysisUsage.completion_tokens ?? analysisUsage.output_tokens ?? null;
  const totalTokens = analysisUsage.total_tokens ?? (
    Number.isFinite(Number(inputTokens)) && Number.isFinite(Number(outputTokens))
      ? (Number(inputTokens) + Number(outputTokens))
      : null
  );
  const cacheHit = (
    analysisResult?.cache_hit === true ||
    String(analysisResult?.analysis_source || '').toLowerCase() === 'cache'
  );
  const analysisSourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const analysisSourceClass = cacheHit
    ? 'bg-cyan-100 text-cyan-700'
    : 'bg-slate-200 text-slate-700';

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl sm:text-3xl font-bold text-center text-slate-800 mb-4 sm:mb-6">Auction Insight Prototype</h1>
      <p className="text-center text-slate-600 text-sm sm:text-base mb-3 sm:mb-4">Upload an auction image to identify the item and analyze margins.</p>
      
      <div className="mb-4 sm:mb-8 bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <div className="flex items-center gap-2 min-w-0 text-slate-700">
            <Settings size={18} className="shrink-0" />
            <span className="font-medium text-sm sm:text-base whitespace-nowrap">Model:</span>
            <select 
              value={modelName}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={isChangingModel}
              className={`w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none text-sm font-medium shadow-sm ${
                isChangingModel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem'
              }}
            >
              <optgroup label="GPT-5 Series (Latest)">
                <option value="gpt-5.4">gpt-5.4</option>
                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                <option value="gpt-5.2">gpt-5.2</option>
                <option value="gpt-5.1">gpt-5.1</option>
                <option value="gpt-5">gpt-5</option>
              </optgroup>
              <optgroup label="Gemini">
                <option value="gemini-flash-latest">gemini-flash-latest</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
              </optgroup>
              <optgroup label="Claude">
                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                <option value="claude-opus-4-6">claude-opus-4-6</option>
                <option value="claude-opus-4-7">claude-opus-4-7</option>
              </optgroup>
              <optgroup label="GPT-4 Series">
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4o-mini">gpt-4o-mini</option>
                <option value="gpt-4.1">gpt-4.1</option>
                <option value="gpt-4.1-mini">gpt-4.1-mini</option>
              </optgroup>
              <optgroup label="Reasoning Models">
                <option value="o1">o1</option>
                <option value="o3-mini">o3-mini</option>
                <option value="o3">o3</option>
                <option value="o4-mini">o4-mini</option>
              </optgroup>
            </select>
            {isChangingModel && <span className="text-slate-400 text-sm shrink-0">Updating...</span>}
          </div>

          <div className="flex items-center gap-2 min-w-0 text-slate-700">
            <span className="font-medium text-sm sm:text-base whitespace-nowrap">Resize:</span>
            <select
              value={resizeMode}
              onChange={(e) => setResizeMode(e.target.value)}
              className="w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none cursor-pointer text-sm font-medium shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem'
              }}
            >
              <option value="original">Original</option>
              <option value="1600">Max 1600px</option>
              <option value="1280">Max 1280px</option>
              <option value="1024">Max 1024px</option>
              <option value="768">Max 768px</option>
              <option value="512">Max 512px</option>
              <option value="128">Max 128px</option>
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-0 text-slate-700">
            <span className="font-medium text-sm sm:text-base whitespace-nowrap">Compression:</span>
            <select
              value={compressionMode}
              onChange={(e) => setCompressionMode(e.target.value)}
              className="w-full min-w-0 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all appearance-none cursor-pointer text-sm font-medium shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem'
              }}
            >
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-0 text-slate-700">
            <span className="font-medium text-sm sm:text-base whitespace-nowrap">eBay RAG:</span>
            <button
              type="button"
              onClick={() => handleEbayRagChange(!useEbayRag)}
              disabled={isChangingEbayRag}
              className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                useEbayRag
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-300 bg-white text-slate-600'
              } ${isChangingEbayRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              <span>{useEbayRag ? 'Enabled' : 'Disabled'}</span>
              <span className="text-xs">{isChangingEbayRag ? 'Updating...' : 'Toggle'}</span>
            </button>
          </div>
          <div className="flex items-center gap-2 min-w-0 text-slate-700">
            <span className="font-medium text-sm sm:text-base whitespace-nowrap">Notes RAG:</span>
            <button
              type="button"
              onClick={() => handleNotesRagChange(!useNotesRag)}
              disabled={isChangingNotesRag}
              className={`w-full inline-flex items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                useNotesRag
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-slate-300 bg-white text-slate-600'
              } ${isChangingNotesRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              <span>{useNotesRag ? 'Enabled' : 'Disabled'}</span>
              <span className="text-xs">{isChangingNotesRag ? 'Updating...' : 'Toggle'}</span>
            </button>
          </div>
        </div>
      </div>

      <div 
        className={`border-2 border-dashed rounded-lg p-4 sm:p-10 text-center cursor-pointer transition-all ${
          isDragOver ? 'border-blue-500 bg-blue-50' : 'border-blue-300 hover:border-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <input 
          type="file" 
          className="hidden" 
          ref={fileInputRef} 
          onChange={handleFileChange} 
          accept=".jpg,.jpeg,.png,.heic,image/jpeg,image/png,image/heic,image/heif"
          multiple
        />
        
        {previewUrls.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 max-w-full">
            {previewUrls.map((preview, index) => (
              <div key={preview.id || `${preview.name}-${index}`} className="relative group aspect-square">
                {preview.isConverting ? (
                  <div className="w-full h-full rounded shadow-sm border border-slate-200 bg-slate-100 flex flex-col items-center justify-center text-slate-500 p-2">
                    <Loader2 size={22} className="animate-spin" />
                    <span className="mt-2 text-[10px] text-slate-500 text-center leading-tight">
                      Generating preview...
                    </span>
                  </div>
                ) : preview.hasPreviewError ? (
                  <div className="w-full h-full rounded shadow-sm border border-slate-200 bg-slate-100 flex flex-col items-center justify-center text-slate-500 p-2">
                    <ImageIcon size={22} />
                    <span className="mt-1 text-[10px] font-semibold uppercase">{preview.extension || 'img'}</span>
                    {preview.isHeic && (
                      <span className="mt-1 text-[10px] text-slate-400 text-center leading-tight">
                        HEIC preview unavailable
                      </span>
                    )}
                  </div>
                ) : (
                  <img
                    src={preview.url}
                    alt={`Preview ${index}`}
                    className="w-full h-full object-cover rounded shadow-sm"
                    onError={() => markPreviewError(index)}
                  />
                )}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <div className="border-2 border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-slate-400 aspect-square hover:border-blue-400 transition-colors">
               <ImageIcon size={24} />
               <span className="text-[10px] mt-1">Add More</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center text-slate-400">
            <Upload size={48} className="mb-2" />
            <p>Click or drag JPG, PNG, or HEIC images here (multiple supported)</p>
          </div>
        )}
      </div>

      <button
        className={`w-full mt-6 py-3 px-6 rounded-md font-semibold text-white transition-colors ${
          selectedFiles.length === 0 || loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        disabled={selectedFiles.length === 0 || loading}
        onClick={handleAnalyze}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <Loader2 className="animate-spin mr-2" /> Analyzing...
          </span>
        ) : 'Analyze Image'}
      </button>

      {analysisResult && (
        <div className="mt-4 sm:mt-8">
          <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2 sm:mb-3">Analysis Results:</h3>
          <div className="p-3 sm:p-6 bg-slate-50 rounded-lg border border-slate-200">
            <div className="mb-3 sm:mb-4">
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="bg-slate-200 text-slate-700 px-2 py-1 rounded">
                  Model: {analysisResult.model || modelName || 'N/A'}
                </span>
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                  Resize: {formatResizeDisplay(analysisResult.resize_mode)}
                </span>
                <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">
                  Compression: {analysisResult.compression_mode || 'N/A'}
                </span>
                <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded">
                  eBay RAG: {(
                    analysisResult.use_ebay_rag !== undefined
                      ? Boolean(analysisResult.use_ebay_rag)
                      : useEbayRag
                  )
                    ? `on (${Number(analysisResult.ebay_rag_result_count || 0)} refs)`
                    : 'off'}
                </span>
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                  Notes RAG: {(
                    analysisResult.use_notes_rag !== undefined
                      ? Boolean(analysisResult.use_notes_rag)
                      : useNotesRag
                  )
                    ? `on (${Number(analysisResult.notes_rag_example_count || 0)} examples)`
                    : 'off'}
                </span>
                {((analysisResult.analysis_duration_ms !== undefined && analysisResult.analysis_duration_ms !== null) || 
                  (analysisResult.duration_ms !== undefined && analysisResult.duration_ms !== null)) && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    Speed: {Number(analysisResult.analysis_duration_ms || analysisResult.duration_ms).toFixed(0)}ms
                  </span>
                )}
                {(
                  analysisResult.analysis_cost_display !== undefined ||
                  analysisResult.cost_display !== undefined ||
                  analysisResult.analysis_cost !== undefined ||
                  analysisResult.cost !== undefined
                ) && (
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    Cost: {
                      analysisResult.analysis_cost_display ||
                      analysisResult.cost_display ||
                      ((analysisResult.analysis_cost !== undefined && analysisResult.analysis_cost !== null)
                        ? `$${Number(analysisResult.analysis_cost).toFixed(4)}`
                        : ((analysisResult.cost !== undefined && analysisResult.cost !== null)
                          ? `$${Number(analysisResult.cost).toFixed(4)}`
                          : 'N/A'))
                    }
                  </span>
                )}
                <span className={`px-2 py-1 rounded font-bold ${analysisSourceClass}`}>
                  Src: {analysisSourceLabel}
                </span>
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  In Tok: {formatTokenCount(inputTokens)}
                </span>
                <span className="bg-violet-100 text-violet-700 px-2 py-1 rounded">
                  Out Tok: {formatTokenCount(outputTokens)}
                </span>
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                  Tot Tok: {formatTokenCount(totalTokens)}
                </span>
              </div>
            </div>
          <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
            <div className="w-full md:w-1/2 flex flex-col gap-3 sm:gap-4">
              <div className="relative aspect-video bg-slate-900 rounded overflow-hidden flex items-center justify-center group cursor-pointer" onClick={() => setIsModalOpen(true)}>
                <div className="relative">
                  <img 
                    src={analysisDisplayImageUrls[currentImageIndex]} 
                    alt="Analyzed" 
                    className="max-w-full max-h-full transition-all group-hover:brightness-90 block" 
                    onError={() => markResultImageError(currentImageIndex)}
                  />
                  
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 flex items-center justify-center transition-all z-10 pointer-events-none">
                     <div className="bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                       <ZoomIn size={20} className="text-blue-600" />
                     </div>
                  </div>

                  {/* Bounding Boxes for the current image */}
                  {(analysisResult.items || []).map((item, idx) => {
                    const colorClass = colors[idx % colors.length];
                    const [borderColor, bgColor] = colorClass.split(' ');
                    
                    let currentBox = null;
                    let currentLabel = item.item;

                    if (item.bounding_boxes && Array.isArray(item.bounding_boxes)) {
                      const bbEntry = item.bounding_boxes[currentImageIndex];
                      if (bbEntry && typeof bbEntry === 'object' && bbEntry.box) {
                        currentBox = bbEntry.box;
                        currentLabel = bbEntry.label || currentLabel;
                      } else {
                        currentBox = bbEntry;
                      }
                    } else if (currentImageIndex === 0) {
                      currentBox = item.bounding_box;
                    }

                    // Ignore [0,0,0,0] boxes which might be returned by some models despite instructions
                    if (Array.isArray(currentBox) && currentBox.every(val => val === 0)) {
                      currentBox = null;
                    }

                    return currentBox && (
                      <div 
                        key={`bb-${idx}`}
                        className={`absolute border-2 ${borderColor} transition-all pointer-events-none`}
                        style={{
                          top: `${currentBox[0] / 10}%`,
                          left: `${currentBox[1] / 10}%`,
                          height: `${(currentBox[2] - currentBox[0]) / 10}%`,
                          width: `${(currentBox[3] - currentBox[1]) / 10}%`,
                        }}
                      >
                        <span className={`absolute -top-5 left-0 ${bgColor} text-white text-[8px] px-1 rounded whitespace-nowrap shadow-sm`}>
                          {currentLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation arrows if multiple images */}
                {analysisDisplayImageUrls.length > 1 && (
                  <>
                    {currentImageIndex > 0 && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(currentImageIndex - 1); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-50 p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 transition-all shadow-lg"
                      >
                        <ChevronLeft size={20} />
                      </button>
                    )}
                    {currentImageIndex < analysisDisplayImageUrls.length - 1 && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(currentImageIndex + 1); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-50 p-1.5 bg-black/40 text-white rounded-full hover:bg-black/60 transition-all shadow-lg"
                      >
                        <ChevronRight size={20} />
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Thumbnail strip */}
              {analysisDisplayImageUrls.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {analysisDisplayImageUrls.map((url, idx) => (
                    <img 
                      key={idx}
                      src={url}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-16 h-16 object-cover rounded cursor-pointer transition-all border-2 ${currentImageIndex === idx ? 'border-blue-500 scale-105 shadow-sm' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      alt={`Thumb ${idx}`}
                      onError={() => markResultImageError(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="w-full md:w-1/2 space-y-3 sm:space-y-4">
              {(analysisResult.items || []).map((item, idx) => {
                const isDirty = item.id && originalPrices[item.id] !== undefined && parseFloat(originalPrices[item.id]) !== parseFloat(item.actual_price);
                const isUpdating = updatingId === item.id;
                const isNameDirty = item.id && originalNames[item.id] !== undefined && originalNames[item.id] !== (item.manual_name || '');
                const isNameUpdating = updatingNameId === item.id;

                return (
                  <div key={idx} className="bg-white p-3 sm:p-4 rounded border border-slate-200 shadow-sm hover:border-blue-300 transition-colors">
                    <strong className="block text-slate-700 text-base sm:text-lg mb-1">
                      {item.bounding_boxes && Array.isArray(item.bounding_boxes) && item.bounding_boxes[currentImageIndex] && item.bounding_boxes[currentImageIndex].label 
                        ? item.bounding_boxes[currentImageIndex].label 
                        : item.item}
                    </strong>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                      <span className="text-slate-500 text-xs sm:text-sm">Manual Name:</span>
                      <div className="relative flex items-center flex-1">
                        <input
                          type="text"
                          placeholder="Enter custom name..."
                          className={`w-full pr-8 py-1 px-2 border rounded text-sm text-slate-700 focus:outline-none focus:ring-1 transition-all ${
                            isNameDirty
                              ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30'
                              : 'border-slate-200 focus:ring-green-500'
                          }`}
                          value={item.manual_name || ''}
                          onChange={(e) => handleManualNameChange(item.id, e.target.value)}
                        />
                        {item.id && (
                          <button
                            onClick={() => updateItemName(item.id, item.manual_name || '')}
                            disabled={!isNameDirty || isNameUpdating}
                            className={`absolute right-1 p-1 rounded transition-all ${
                              isNameUpdating
                                ? 'text-slate-400'
                                : isNameDirty
                                  ? 'text-blue-600 hover:bg-blue-100'
                                  : 'text-green-500 cursor-default'
                            }`}
                            title={isNameDirty ? "Save Name" : "Name Saved"}
                          >
                            {isNameUpdating ? (
                              <RefreshCcw size={14} className="animate-spin" />
                            ) : isNameDirty ? (
                              <Save size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col space-y-2">
                      <p className="text-slate-600 text-sm sm:text-base">Estimated Value: <span className="font-bold text-green-600">${item.estimated_value}</span></p>
                      <div className="flex items-center space-x-2">
                        <span className="text-slate-600 text-xs sm:text-sm">Actual Price:</span>
                        <div className="relative flex items-center">
                          <span className="absolute left-2 text-slate-400 text-sm">$</span>
                          <input 
                            type="number" 
                            placeholder="0.00"
                            className={`w-24 pl-5 pr-8 py-1 border rounded text-sm focus:outline-none focus:ring-1 transition-all ${
                              isDirty 
                                ? 'border-blue-300 focus:ring-blue-500 bg-blue-50/30' 
                                : 'border-slate-200 focus:ring-green-500'
                            }`}
                            value={item.actual_price}
                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                            step="0.01"
                          />
                          {item.id && (
                            <button 
                              onClick={() => updatePrice(item.id, item.actual_price)}
                              disabled={!isDirty || isUpdating}
                              className={`absolute right-1 p-1 rounded transition-all ${
                                isUpdating
                                  ? 'text-slate-400'
                                  : isDirty
                                    ? 'text-blue-600 hover:bg-blue-100'
                                    : 'text-green-500 cursor-default'
                              }`}
                              title={isDirty ? "Save Price" : "Price Saved"}
                            >
                              {isUpdating ? (
                                <RefreshCcw size={14} className="animate-spin" />
                              ) : isDirty ? (
                                <Save size={14} />
                              ) : (
                                <Check size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const condition = item?.condition || 'Unknown';
                          const priceSource = item?.price_source || 'model_knowledge';
                          const sourceLabel = priceSource === 'ebay_rag' ? 'eBay RAG' : priceSource === 'image' ? 'Image' : 'Model Knowledge';
                          const sourceBg = priceSource === 'ebay_rag' ? 'bg-blue-100 text-blue-700' : priceSource === 'image' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                          const condBg = condition === 'New' ? 'bg-green-100 text-green-700' : condition === 'Damaged' || condition === 'For Parts' ? 'bg-red-100 text-red-700' : condition === 'Unknown' ? 'bg-slate-100 text-slate-500' : 'bg-yellow-100 text-yellow-700';
                          return (
                            <>
                              <span className="text-[9px] text-slate-400">Condition:</span>
                              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${condBg}`}>{condition}</span>
                              <span className="text-[9px] text-slate-400 ml-1">AI Src:</span>
                              <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${sourceBg}`}>{sourceLabel}</span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
              {(analysisResult.items || []).length >= 1 && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Est. Sum</span>
                  <span className="text-base text-green-600 font-bold">${(analysisResult.items || []).reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* eBay Similar Items Section */}
          {(ebayLoading || ebayResults) && (
            <div className="mt-4 sm:mt-6 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart size={18} className="text-blue-600" />
                <h4 className="text-base sm:text-lg font-semibold text-slate-800">Similar Items on eBay</h4>
                {ebayLoading && <Loader2 size={16} className="animate-spin text-blue-500" />}
              </div>
              {ebayLoading && !ebayResults && (
                <p className="text-sm text-slate-500 italic">Searching eBay for similar items...</p>
              )}
              {ebayResults && ebayResults.length === 0 && (
                <p className="text-sm text-slate-500 italic">No similar items found on eBay.</p>
              )}
              {ebayResults && ebayResults.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {ebayResults.map((ebayItem, idx) => (
                    <a
                      key={ebayItem.itemId || idx}
                      href={ebayItem.itemWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md hover:border-blue-300 transition-all group"
                    >
                      {ebayItem.image && (
                        <div className="aspect-square bg-slate-100 overflow-hidden">
                          <img src={ebayItem.image} alt={ebayItem.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </div>
                      )}
                      <div className="p-2">
                        <p className="text-xs font-medium text-slate-700 line-clamp-2 mb-1">{ebayItem.title}</p>
                        <p className="text-sm font-bold text-green-600">${ebayItem.price} {ebayItem.currency}</p>
                        {ebayItem.condition && (
                          <p className="text-[10px] text-slate-400 mt-0.5">{ebayItem.condition}</p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      )}
      
      <ImageModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        imageUrl={analysisResult ? (analysisDisplayImageUrls[currentImageIndex] || null) : null}
        imageUrls={analysisResult ? analysisDisplayImageUrls : []}
        items={analysisResult?.items}
      />
    </div>
  );
};

export default AnalysisPage;
